import React from 'react';
import { BlockButtonStyle, BlockStyle, LandingBlock, MobileBlockStyle } from '../../types';
import { BUTTON_ICONS, FONT_WEIGHTS } from '../../data/fonts';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
interface StyleSettingsProps {
  block: LandingBlock;
  onChange: (block: LandingBlock) => void;
}
const DEFAULT_STYLE: BlockStyle = {
  background: 'transparente',
  align: 'center',
  shadow: 'ninguna',
  maxWidth: 'normal'
};

/** Deslizador con valor numérico visible. */
function Slider({
  id,
  label,
  min,
  max,
  step = 1,
  suffix = 'px',
  value,
  onChange
}: {
  id: string;
  label: string;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  value: number | undefined;
  onChange: (value: number) => void;
}) {
  return <div data-id="element-348" data-mp="17o2cnt0kxe2o">
      <div className="mb-1.5 flex items-center justify-between" data-id="element-349" data-mp="17natxis2zatt">
        <label htmlFor={id} className="text-sm font-medium text-foreground" data-id="element-350" data-mp="1cfu5ybb4dxwi">
          {label}
        </label>
        <span className="text-xs tabular-nums text-muted-foreground" data-id="element-351" data-mp="2spj2rzgzumi5">
          {value === undefined ? 'Auto' : `${value}${suffix}`}
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value ?? min} onChange={e => onChange(Number(e.target.value))} className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary" data-id="element-352" data-mp="32eao3orju8k3" />
    </div>;
}

/** Editor visual de estilo, tipografía y botón de un bloque. */
export function StyleSettings({
  block,
  onChange
}: StyleSettingsProps) {
  const style: BlockStyle = {
    ...DEFAULT_STYLE,
    ...(block.style ?? {})
  };
  const button: BlockButtonStyle = block.button ?? {};
  const mobile: MobileBlockStyle = style.mobile ?? {
    sync: true
  };
  const tablet: MobileBlockStyle = style.tablet ?? {
    sync: true
  };
  function setDevice(key: 'mobile' | 'tablet', partial: Partial<MobileBlockStyle>) {
    const current = key === 'mobile' ? mobile : tablet;
    onChange({
      ...block,
      style: {
        ...style,
        [key]: {
          ...current,
          ...partial
        }
      }
    });
  }
  function setMobile(partial: Partial<MobileBlockStyle>) {
    setDevice('mobile', partial);
  }
  function setStyle(partial: Partial<BlockStyle>) {
    onChange({
      ...block,
      style: {
        ...style,
        ...partial
      }
    });
  }
  function setButton(partial: Partial<BlockButtonStyle>) {
    onChange({
      ...block,
      button: {
        ...button,
        ...partial
      }
    });
  }
  return <div className="space-y-6" data-id="element-353" data-mp="dsii7cjy7bhy">
      <section className="space-y-4" data-id="element-354" data-mp="il1wycudgcoc">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-355" data-mp="im3i5a53qfjs">
          Fondo y caja
        </h4>
        <Select id={`${block.id}-bg`} label="Fondo" options={[{
        value: 'transparente',
        label: 'Transparente'
      }, {
        value: 'blanco',
        label: 'Blanco'
      }, {
        value: 'gris',
        label: 'Gris claro'
      }, {
        value: 'marca',
        label: 'Color de marca'
      }, {
        value: 'oscuro',
        label: 'Oscuro'
      }]} value={style.background ?? 'transparente'} onChange={e => setStyle({
        background: e.target.value as BlockStyle['background']
      })} data-id="element-356" data-mp="mozu4i04zder" />
        <Slider id={`${block.id}-py`} label="Espaciado vertical" min={0} max={160} step={4} value={style.paddingY} onChange={paddingY => setStyle({
        paddingY
      })} data-id="element-357" data-mp="sq0dqg5gkw6e" />
        <Slider id={`${block.id}-px`} label="Espaciado horizontal" min={0} max={120} step={4} value={style.paddingX} onChange={paddingX => setStyle({
        paddingX
      })} data-id="element-358" data-mp="1kgoe3pzb5xj0" />
        <Slider id={`${block.id}-radius`} label="Radio de bordes" min={0} max={48} value={style.radius} onChange={radius => setStyle({
        radius
      })} data-id="element-359" data-mp="1fnvax6jhmx91" />
        <Select id={`${block.id}-shadow`} label="Sombra" options={[{
        value: 'ninguna',
        label: 'Sin sombra'
      }, {
        value: 'suave',
        label: 'Suave'
      }, {
        value: 'media',
        label: 'Media'
      }, {
        value: 'fuerte',
        label: 'Fuerte'
      }]} value={style.shadow ?? 'ninguna'} onChange={e => setStyle({
        shadow: e.target.value as BlockStyle['shadow']
      })} data-id="element-360" data-mp="287aldf1hndry" />
      </section>

      <section className="space-y-4 border-t border-border pt-5" data-id="element-361" data-mp="1ez75x1wevc6x">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-362" data-mp="1iyy4vsw3cymj">
          Tipografía
        </h4>
        <Select id={`${block.id}-align`} label="Alineación" options={[{
        value: 'left',
        label: 'Izquierda'
      }, {
        value: 'center',
        label: 'Centro'
      }, {
        value: 'right',
        label: 'Derecha'
      }]} value={style.align ?? 'center'} onChange={e => setStyle({
        align: e.target.value as BlockStyle['align']
      })} data-id="element-363" data-mp="26qd2j8cxarrg" />
        <Select id={`${block.id}-weight`} label="Grosor" placeholder="Automático" options={FONT_WEIGHTS} value={style.titleWeight ? String(style.titleWeight) : ''} onChange={e => setStyle({
        titleWeight: e.target.value ? Number(e.target.value) : undefined
      })} data-id="element-364" data-mp="2pllbs8xejs26" />
        <Slider id={`${block.id}-tracking`} label="Espaciado entre letras" min={-2} max={8} step={0.5} value={style.letterSpacing} onChange={letterSpacing => setStyle({
        letterSpacing
      })} data-id="element-365" data-mp="dyhw9608ldel" />
        <Input id={`${block.id}-color`} label="Color del texto" type="color" value={style.textColor ?? '#0B0B0F'} onChange={e => setStyle({
        textColor: e.target.value
      })} data-id="element-366" data-mp="nqnjk2fjn6ng" />
        <Switch id={`${block.id}-upper`} label="Texto en mayúsculas" checked={Boolean(style.uppercase)} onChange={uppercase => setStyle({
        uppercase
      })} data-id="element-367" data-mp="3r6uki16kqpsj" />
      </section>

      {/* Ajustes que solo afectan a tablet (641–1024 px). */}
      <section className="space-y-4 border-t border-border pt-5" data-id="element-368" data-mp="50xrhn360yz8">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-369" data-mp="1lbrwh26pasiu">
          Tablet
        </h4>
        <Switch id={`${block.id}-t-sync`} label="Usar los mismos valores en todos los dispositivos" checked={tablet.sync !== false} onChange={sync => setDevice('tablet', {
        sync
      })} data-id="element-370" data-mp="1gdqc8dxjgn5s" />
        {tablet.sync === false && <div className="space-y-4 rounded-xl bg-surface p-4" data-id="element-371" data-mp="1xfe4bgvf26pj">
            <p className="text-xs text-muted-foreground" data-id="element-372" data-mp="1xfxeuciqlstj">
              Estos valores sustituyen solo la versión de tablet. Escritorio y
              móvil no cambian.
            </p>
            <Slider id={`${block.id}-t-title`} label="Tamaño del título" min={22} max={72} value={tablet.titleSize} onChange={titleSize => setDevice('tablet', {
          titleSize
        })} data-id="element-373" data-mp="14s1ff01s9mt2" />
            <Slider id={`${block.id}-t-body`} label="Tamaño del texto" min={12} max={28} value={tablet.bodySize} onChange={bodySize => setDevice('tablet', {
          bodySize
        })} data-id="element-374" data-mp="1eo4hk5wmxlg0" />
            <Slider id={`${block.id}-t-lh`} label="Altura de línea" min={0.95} max={1.8} step={0.01} suffix="" value={tablet.lineHeight} onChange={lineHeight => setDevice('tablet', {
          lineHeight
        })} data-id="element-375" data-mp="lz5z17oloha6" />
            <Slider id={`${block.id}-t-ls`} label="Espaciado entre letras" min={-2} max={6} step={0.5} value={tablet.letterSpacing} onChange={letterSpacing => setDevice('tablet', {
          letterSpacing
        })} data-id="element-376" data-mp="3491tmjw8q6gw" />
            <Select id={`${block.id}-t-weight`} label="Grosor del título" placeholder="Automático" options={FONT_WEIGHTS} value={tablet.titleWeight ? String(tablet.titleWeight) : ''} onChange={e => setDevice('tablet', {
          titleWeight: e.target.value ? Number(e.target.value) : undefined
        })} data-id="element-377" data-mp="2vazy2jva0610" />
            <Select id={`${block.id}-t-align`} label="Alineación" placeholder="Como en escritorio" options={[{
          value: 'left',
          label: 'Izquierda'
        }, {
          value: 'center',
          label: 'Centro'
        }, {
          value: 'right',
          label: 'Derecha'
        }]} value={tablet.align ?? ''} onChange={e => setDevice('tablet', {
          align: (e.target.value || undefined) as BlockStyle['align']
        })} data-id="element-378" data-mp="2gi663yythxfg" />
            <Slider id={`${block.id}-t-py`} label="Margen vertical" min={0} max={120} step={2} value={tablet.paddingY} onChange={paddingY => setDevice('tablet', {
          paddingY
        })} data-id="element-379" data-mp="3mgdla3d36d4p" />
            <Slider id={`${block.id}-t-px`} label="Padding lateral" min={0} max={64} step={2} value={tablet.paddingX} onChange={paddingX => setDevice('tablet', {
          paddingX
        })} data-id="element-380" data-mp="3cg3bksq9fnua" />
          </div>}
      </section>

      {/* Ajustes que solo afectan a móvil (≤ 640 px). */}
      <section className="space-y-4 border-t border-border pt-5" data-id="element-381" data-mp="37ibivwhsjxp1">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-382" data-mp="37jwapecq12j7">
          Móvil
        </h4>
        <Switch id={`${block.id}-m-sync`} label="Usar los mismos valores en todos los dispositivos" checked={mobile.sync !== false} onChange={sync => setMobile({
        sync
      })} data-id="element-383" data-mp="2yp87ruumbwxk" />
        {mobile.sync === false && <div className="space-y-4 rounded-xl bg-surface p-4" data-id="element-384" data-mp="2ortto1w63k8f">
            <p className="text-xs text-muted-foreground" data-id="element-385" data-mp="255s9nlyioc2m">
              Estos valores sustituyen solo la versión móvil. Escritorio y tablet
              no cambian.
            </p>
            <Slider id={`${block.id}-m-title`} label="Tamaño del título" min={22} max={64} value={mobile.titleSize} onChange={titleSize => setMobile({
          titleSize
        })} data-id="element-386" data-mp="2169r2wu64d2y" />
            <Slider id={`${block.id}-m-body`} label="Tamaño del texto" min={12} max={26} value={mobile.bodySize} onChange={bodySize => setMobile({
          bodySize
        })} data-id="element-387" data-mp="2a3jz89bovm3n" />
            <Slider id={`${block.id}-m-lh`} label="Altura de línea" min={0.95} max={1.8} step={0.01} suffix="" value={mobile.lineHeight} onChange={lineHeight => setMobile({
          lineHeight
        })} data-id="element-388" data-mp="2k29io6j8hem0" />
            <Slider id={`${block.id}-m-ls`} label="Espaciado entre letras" min={-2} max={6} step={0.5} value={mobile.letterSpacing} onChange={letterSpacing => setMobile({
          letterSpacing
        })} data-id="element-389" data-mp="2svwd9tbpgbtm" />
            <Select id={`${block.id}-m-weight`} label="Grosor del título" placeholder="Automático" options={FONT_WEIGHTS} value={mobile.titleWeight ? String(mobile.titleWeight) : ''} onChange={e => setMobile({
          titleWeight: e.target.value ? Number(e.target.value) : undefined
        })} data-id="element-390" data-mp="32vdhe6bb6hnh" />
            <Select id={`${block.id}-m-align`} label="Alineación" placeholder="Como en escritorio" options={[{
          value: 'left',
          label: 'Izquierda'
        }, {
          value: 'center',
          label: 'Centro'
        }, {
          value: 'right',
          label: 'Derecha'
        }]} value={mobile.align ?? ''} onChange={e => setMobile({
          align: (e.target.value || undefined) as BlockStyle['align']
        })} data-id="element-391" data-mp="3ndcsi86fz9cy" />
            <Slider id={`${block.id}-m-py`} label="Margen vertical" min={0} max={96} step={2} value={mobile.paddingY} onChange={paddingY => setMobile({
          paddingY
        })} data-id="element-392" data-mp="5x4nkl3uo2o6" />
            <Slider id={`${block.id}-m-px`} label="Padding lateral" min={0} max={40} step={2} value={mobile.paddingX} onChange={paddingX => setMobile({
          paddingX
        })} data-id="element-393" data-mp="2qmu20x0n5mgc" />
          </div>}
      </section>

      <section className="space-y-4 border-t border-border pt-5" data-id="element-394" data-mp="1b3cx4z29brfv">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-395" data-mp="1b1ibfij5vvaq">
          Botón
        </h4>
        <Input id={`${block.id}-btn-label`} label="Texto del botón" placeholder="Usar el texto del contenido" value={button.label ?? ''} onChange={e => setButton({
        label: e.target.value
      })} data-id="element-396" data-mp="1jvy3tgsm67oc" />
        <div className="grid grid-cols-2 gap-3" data-id="element-397" data-mp="1ttuniwzmaay2">
          <Input id={`${block.id}-btn-color`} label="Color" type="color" value={button.color ?? '#3D8BFF'} onChange={e => setButton({
          color: e.target.value
        })} data-id="element-398" data-mp="1tsj8u24mvis6" />
          <Input id={`${block.id}-btn-hover`} label="Color hover" type="color" value={button.hoverColor ?? '#2F6FD1'} onChange={e => setButton({
          hoverColor: e.target.value
        })} data-id="element-399" data-mp="22tr74k75gjm9" />
          <Input id={`${block.id}-btn-text`} label="Color del texto" type="color" value={button.textColor ?? '#FFFFFF'} onChange={e => setButton({
          textColor: e.target.value
        })} data-id="element-400" data-mp="8hsvycpa5dnq" />
          <Select id={`${block.id}-btn-icon`} label="Icono" options={BUTTON_ICONS} value={button.icon ?? ''} onChange={e => setButton({
          icon: e.target.value
        })} data-id="element-401" data-mp="16tvotb11fszw" />
        </div>
        <Slider id={`${block.id}-btn-radius`} label="Radio de bordes" min={0} max={40} value={button.radius} onChange={radius => setButton({
        radius
      })} data-id="element-402" data-mp="1gpys4k7mlxof" />
        <Slider id={`${block.id}-btn-height`} label="Altura" min={36} max={80} value={button.height} onChange={height => setButton({
        height
      })} data-id="element-403" data-mp="1przntcn7obnz" />
        <Slider id={`${block.id}-btn-padding`} label="Padding horizontal" min={12} max={80} value={button.paddingX} onChange={paddingX => setButton({
        paddingX
      })} data-id="element-404" data-mp="24ft3syg7lg73" />
        <Select id={`${block.id}-btn-anim`} label="Animación" options={[{
        value: 'ninguna',
        label: 'Sin animación'
      }, {
        value: 'elevar',
        label: 'Elevar al pasar'
      }, {
        value: 'escalar',
        label: 'Escalar al pasar'
      }, {
        value: 'pulso',
        label: 'Pulso'
      }]} value={button.animation ?? 'elevar'} onChange={e => setButton({
        animation: e.target.value as BlockButtonStyle['animation']
      })} data-id="element-405" data-mp="2di2djm3nl5jf" />
        <Switch id={`${block.id}-btn-full`} label="Ancho completo" checked={Boolean(button.fullWidth)} onChange={fullWidth => setButton({
        fullWidth
      })} data-id="element-406" data-mp="2n44ziz2ig5qm" />
        <Switch id={`${block.id}-btn-shadow`} label="Sombra y brillo" checked={button.shadow !== false} onChange={shadow => setButton({
        shadow
      })} data-id="element-407" data-mp="2e486ymd02mqa" />
      </section>
    </div>;
}